"use client";

import FormTextField from "@/src/components/common/broadsheet/form-text-field";
import SchemaWrapper from "@/src/components/common/form/SchemaWrapper";
import {
  Alert,
  Box,
  Button,
  Chip,
  FormHelperText,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import { Controller, useFormContext } from "react-hook-form";
import { ChangeEvent, useEffect, useRef, useState } from "react";
import ReactHtmlParser from "@/src/utils/html-parser";
import { getBaseUrlForCSR } from "@/src/utils/fetch-helpers";
import SocialIcons from "@/src/components/common/pages/social-icons/social-icons";
import VISUALLY_HIDDEN from "@/src/utils/visually-hidden";
import {
  captchaButtonSx,
  captchaFieldSx,
  captchaFrameSx,
  captchaImageStyle,
  fieldSx,
  FormMasthead,
  FormSection,
  requiredLabel,
  StandingNote,
} from "@/src/components/common/broadsheet/form-kit";
import {
  actionSx,
  doubleRule,
  FONT_UI,
  SHEET_GUTTER,
} from "@/src/components/common/broadsheet/tokens";
import { journalApiBase } from "@/src/utils/journal-api-base";

const ticketTypes = [
  "Submission",
  "Review Process",
  "Publishing Charges",
  "Invoice & Payments",
  "Language Edit",
  "Track Accepted Article",
  "Galley Proof",
  "Indexing",
  "Other",
];

const roles = [
  "Author",
  "Reviewer",
  "Associated Editor",
  "Editor-In-Chief",
  "Other",
];

const supportInfoDictionary = "journal.support.form.support";

const ACCEPTED_FILES = ".pdf,.doc,.docx,.zip,.gif,.jpg,.jpeg,.png,.bmp,.xls,.xlsx,.csv";

const schemaConfig = {
  journal: {
    type: "string",
    label: "Journal",
  },
  role: {
    type: "string",
    label: "Role",
    required: true,
  },
  full_name: {
    type: "string",
    label: "Full name",
    required: true,
  },
  email: {
    type: "string",
    label: "Email",
    required: true,
    email: true,
  },
  phone_number: {
    type: "string",
    label: "Phone number",
    required: true,
  },
  type: {
    type: "string",
    label: "Request type",
    required: true,
  },
  manuscript_id: {
    type: "string",
    label: "Manuscript ID",
  },
  description: {
    type: "string",
    label: "Description",
    required: true,
  },
  captcha_phrase: {
    type: "string",
    label: "Captcha",
    required: true,
  },
} as SchemaConfig;

const asBodyCopy = (html: string) =>
  html
    .replace(/<(\/?)h[1-6]([^>]*)>/gi, "<$1p$2>")
    .replace(/[\u{1F300}-\u{1FAFF}\u{2600}-\u{27BF}]/gu, "")
    .replace(/<p([^>]*)>\s+/gi, "<p$1>");

const noteSx = {
  "& p": { m: 0 },
  "& ul, & ol": { my: 0.25, pl: 1.25 },
  "& li": { mb: 0.125 },
  "& img": { verticalAlign: "middle" },
};

type SelectFieldProps = {
  name: string;
  label: string;
  options: string[];
  isRequired?: boolean;
};

const SelectField = ({
  name,
  label,
  options,
  isRequired = false,
}: SelectFieldProps) => {
  const { control } = useFormContext();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <TextField
          {...field}
          select
          fullWidth
          size="small"
          margin="normal"
          label={requiredLabel(label, isRequired)}
          error={!!error}
          helperText={error?.message}
          value={field.value ?? ""}
          sx={fieldSx}
        >
          {options.map((option) => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </TextField>
      )}
    />
  );
};

const getApiMessage = (payload: any) => {
  const errors = payload?.data?.errors;

  if (errors && typeof errors === "object") {
    const firstError = Object.values(errors)[0];

    if (Array.isArray(firstError)) {
      return firstError[0];
    }

    if (typeof firstError === "string") {
      return firstError;
    }
  }

  return (
    payload?.data?.message ||
    payload?.message ||
    "Unable to submit your support request."
  );
};

export default function SupportForm({
  journalTitle,
  journalAdminUrl,
  isMainJournal,
  journals,
}: {
  journalTitle: string;
  journalAdminUrl: string;
  isMainJournal: boolean;
  journals: Journal[];
}) {
  const formRef = useRef<SchemaWrapperRef>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [captcha, setCaptcha] = useState<Captcha>({ captcha: "", key: "" });
  const [files, setFiles] = useState<File[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState<FormMessage | null>(null);
  const [supportInfo, setSupportInfo] = useState("");

  const captchaUrl = `${getBaseUrlForCSR()}/api/captcha`;
  const apiBase = journalApiBase(journalAdminUrl) ?? "";
  const supportUrl = `${apiBase}/v1/app/support/tickets`;
  const supportInfoUrl = `${apiBase}/v1/dictionary/get/${supportInfoDictionary}`;
  const journalOptions = journals
    .filter((journal) => !journal.is_main_site)
    .map((journal) => journal.title);
  const supportSchemaConfig = {
    ...schemaConfig,
    journal: {
      ...schemaConfig.journal,
      required: isMainJournal,
    },
  } as SchemaConfig;

  const getCaptcha = async () => {
    try {
      const response = await fetch(captchaUrl);

      if (response.ok) {
        const body = await response.json();
        setCaptcha(body.data);
      }
    } catch (error) {
      console.error("Error fetching captcha:", error);
    }
  };

  useEffect(() => {
    getCaptcha();
    getSupportInfo();
  }, []);

  const getSupportInfo = async () => {
    try {
      // const response = await fetch(supportInfoUrl);
      //
      // if (response.ok) {
      //     const body = await response.json();
      //     const value = body?.data?.value;
      //
      //     if (value && value !== supportInfoDictionary) {
      //         setSupportInfo(value);
      //     }
      // }

      setSupportInfo(
        '<h5>🎧 There are three options to contact our support:</h5><ul><li>Fill out the form below:&nbsp;<ol><li><strong><u>Recommended:</u></strong> &nbsp;After submitting the form below, please remember your #ticket ID for future communications&nbsp;with us.&nbsp;</li><li><strong><u>Recommended:</u></strong> Sign in to one of our journals and "Submit a ticket": (Go to the "Support" menu (on the top-left) and then Click on "New Ticket" )</li><li>Send an email to "support<img src="https://services.brieflands.com/cdn/serve/3163d/46fdc0b1ddb190918bbd3f48d2afb29bce1ca06a/Screenshot%202023-01-25%20132041.jpg" alt="" height="20" width="14">brieflands.com" directly.</li></ol></li></ul>',
      );
    } catch (error) {
      console.error("Error fetching support info:", error);
    }
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(event.target.files ?? []);
    setFiles(selectedFiles);
  };

  const clearFiles = () => {
    setFiles([]);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSubmit = async () => {
    if (!formRef.current || isSubmitting) {
      return;
    }

    const isValid = await formRef.current.validateForm();
    if (!isValid) {
      return;
    }

    const values = formRef.current.getValues();
    const formData = new FormData();

    formData.append("role", values.role);
    formData.append("full_name", values.full_name);
    formData.append("email", values.email);
    formData.append("phone_number", values.phone_number);
    formData.append("type", values.type);
    formData.append("journal", isMainJournal ? values.journal : journalTitle);
    formData.append("description", values.description);
    formData.append("captcha_phrase", values.captcha_phrase);
    formData.append("captcha_key", captcha.key);

    if (values.manuscript_id) {
      formData.append("manuscript_id", values.manuscript_id);
    }

    files.forEach((file) => {
      formData.append("files[]", file);
    });

    setIsSubmitting(true);
    setMessage(null);

    try {
      const response = await fetch(supportUrl, {
        method: "POST",
        body: formData,
      });
      const body = await response.json().catch(() => null);

      if (response.ok) {
        setMessage({
          severity: "success",
          message: `Your support ticket has been submitted.${body?.data?.id ? ` Ticket ID: ${body.data.id}` : ""}`,
        });
        formRef.current.reset();
        clearFiles();
      } else {
        setMessage({
          severity: "error",
          message: getApiMessage(body),
        });
      }
    } catch (error) {
      console.error("Error submitting support ticket:", error);
      setMessage({
        severity: "error",
        message: "Unable to submit your support request.",
      });
    } finally {
      setIsSubmitting(false);
      getCaptcha();
    }
  };

  return (
    <Box sx={{ px: SHEET_GUTTER, pb: { xs: 1.5, md: 2.5 } }}>
      <Box
        sx={{ pt: { xs: 1, md: 1.5 }, pb: { xs: 0.75, md: 1 }, ...doubleRule }}
      >
        <FormMasthead title="Support">
          <SocialIcons direction="column" />
        </FormMasthead>
      </Box>

      <SchemaWrapper
        ref={formRef}
        hasDefaultAction={false}
        config={supportSchemaConfig}
        defaultValues={{
          journal: "",
          role: "",
          full_name: "",
          email: "",
          phone_number: "",
          type: "",
          manuscript_id: "",
          description: "",
          captcha_phrase: "",
        }}
      >
        <Stack spacing={2}>
          {supportInfo && (
            <StandingNote>
              <Box sx={noteSx}>{ReactHtmlParser(asBodyCopy(supportInfo))}</Box>
            </StandingNote>
          )}

          <FormSection
            step={1}
            title="Your details"
            description="Tell us who you are and how the support desk can reach you."
          >
            <Grid container spacing={{ xs: 0, md: 2 }}>
              <Grid size={{ xs: 12, md: 6 }}>
                <FormTextField
                  isRequired
                  name="full_name"
                  label="Full name"
                  placeholder="Enter your full name"
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <FormTextField
                  isRequired
                  name="email"
                  label="Email"
                  type="email"
                  placeholder="Enter your email address"
                  helperText="We reply to this address."
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <FormTextField
                  isRequired
                  name="phone_number"
                  label="Phone number"
                  placeholder="Enter your phone number"
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <SelectField
                  isRequired
                  name="role"
                  label="Your role in the journal"
                  options={roles}
                />
              </Grid>
            </Grid>
          </FormSection>

          <FormSection
            step={2}
            title="Your request"
            description={
              isMainJournal
                ? "Choose the journal your request concerns and what it is about."
                : `Tell us what your request about ${journalTitle} concerns.`
            }
          >
            <Grid container spacing={{ xs: 0, md: 2 }}>
              {isMainJournal && (
                <Grid size={{ xs: 12, md: 6 }}>
                  <SelectField
                    isRequired
                    name="journal"
                    label="Journal"
                    options={journalOptions}
                  />
                </Grid>
              )}
              <Grid size={{ xs: 12, md: 6 }}>
                <SelectField
                  isRequired
                  name="type"
                  label="Ticket is related to"
                  options={ticketTypes}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <FormTextField
                  name="manuscript_id"
                  label="Manuscript ID"
                  placeholder="Enter manuscript ID"
                  type="number"
                  helperText="Optional — speeds up requests about a submission."
                  inputProps={{ min: 1 }}
                />
              </Grid>
            </Grid>
          </FormSection>

          <FormSection
            step={3}
            title="Your question"
            description="Describe the request, and attach anything that helps."
          >
            <Grid container spacing={{ xs: 0, md: 2 }}>
              <Grid size={{ xs: 12 }}>
                <FormTextField
                  isRequired
                  multiline
                  minRows={5}
                  maxRows={10}
                  name="description"
                  label="Your question or query"
                  placeholder="Describe your request"
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Box
                  sx={{
                    mt: 1,
                    p: 1,
                    border: "1px dashed",
                    borderColor: "custom.ruleHairline",
                    borderRadius: 0,
                    backgroundColor: "custom.newsprintRaised",
                  }}
                >
                  <Stack spacing={1}>
                    <Typography
                      component="p"
                      sx={{
                        m: 0,
                        fontFamily: FONT_UI,
                        fontSize: "0.8125rem",
                        fontWeight: 600,
                        color: "custom.ink",
                      }}
                    >
                      Attachments
                    </Typography>
                    <input
                      ref={fileInputRef}
                      multiple
                      type="file"
                      accept={ACCEPTED_FILES}
                      onChange={handleFileChange}
                      aria-label="Attach files"
                      style={VISUALLY_HIDDEN}
                    />
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={1}
                      sx={{ alignItems: { xs: "stretch", sm: "center" } }}
                    >
                      <Button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        sx={{ ...actionSx("outline"), flexShrink: 0 }}
                      >
                        {files.length > 0 ? "Replace files" : "Attach files"}
                      </Button>
                      {files.length > 0 && (
                        <Button
                          variant="text"
                          color="error"
                          type="button"
                          onClick={clearFiles}
                          sx={{
                            ...actionSx("outline", "small"),
                            borderColor: "error.main",
                            color: "error.main",
                            "&:hover": {
                              borderColor: "error.main",
                              color: "error.main",
                              backgroundColor: "custom.newsprint",
                            },
                          }}
                        >
                          Clear
                        </Button>
                      )}
                    </Stack>
                    {files.length > 0 && (
                      <Box
                        sx={{ display: "flex", flexWrap: "wrap", gap: 0.375 }}
                      >
                        {files.map((file) => (
                          <Chip
                            key={`${file.name}-${file.size}`}
                            label={file.name}
                            size="small"
                            variant="outlined"
                            sx={{ maxWidth: "100%", borderRadius: 0 }}
                          />
                        ))}
                      </Box>
                    )}
                    <FormHelperText sx={{ m: 0 }}>
                      PDF, Word, ZIP, image, Excel and CSV files.
                    </FormHelperText>
                  </Stack>
                </Box>
              </Grid>
            </Grid>
          </FormSection>

          <FormSection
            step={4}
            title="Confirm and submit"
            description="Enter the security code to prove you are not a robot."
          >
            <Grid container spacing={{ xs: 0, md: 2 }}>
              <Grid size={{ xs: 12, md: 8 }}>
                <FormTextField
                  isRequired
                  name="captcha_phrase"
                  label="Security code"
                  placeholder="Enter the code shown"
                  inputProps={{ autoComplete: "off" }}
                  sx={captchaFieldSx}
                  endAdornment={
                    <InputAdornment position="end">
                      <Box sx={captchaFrameSx}>
                        {captcha?.captcha && (
                          <img
                            src={captcha.captcha}
                            alt="Security code"
                            style={captchaImageStyle}
                          />
                        )}
                        <Button
                          variant="contained"
                          color="primary"
                          type="button"
                          onClick={getCaptcha}
                          sx={captchaButtonSx}
                        >
                          New code
                        </Button>
                      </Box>
                    </InputAdornment>
                  }
                />
              </Grid>
            </Grid>

            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                alignItems: { xs: "stretch", sm: "center" },
                gap: 2,
                mt: 2,
              }}
            >
              <Button
                variant="contained"
                color="primary"
                type="button"
                disabled={isSubmitting || !captcha.key}
                onClick={handleSubmit}
                sx={actionSx("solid")}
              >
                {isSubmitting ? "Submitting..." : "Submit ticket"}
              </Button>
              <Typography
                component="p"
                sx={{
                  m: 0,
                  fontFamily: FONT_UI,
                  fontSize: "0.8125rem",
                  color: "custom.inkMuted",
                }}
              >
                Keep the ticket ID from the confirmation for follow-ups.
              </Typography>
            </Box>

            {message && (
              <Alert severity={message.severity} sx={{ mt: 2 }}>
                <Typography variant="body2">{message.message}</Typography>
              </Alert>
            )}
          </FormSection>
        </Stack>
      </SchemaWrapper>
    </Box>
  );
}
